<!-- views/BlogPost.vue -->
<template>
  <v-container class="blog-post-container mt-8">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <!-- Botón volver -->
        <v-btn text color="primary" @click="$router.go(-1)" class="mb-4">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver al blog
        </v-btn>

        <!-- Contenido del artículo -->
        <div v-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-4">Cargando artículo...</p>
        </div>

        <div v-else-if="article">
          <!-- Título -->
          <h1 class="display-1 font-weight-bold mb-4">{{ article.title }}</h1>

          <!-- Metadatos -->
          <div class="article-meta mb-6">
            <v-chip small class="mr-2" color="primary">
              {{ article.category }}
            </v-chip>
            <span class="mr-3">
              <v-icon small left>mdi-calendar</v-icon>
              {{ formatDate(article.date) }}
            </span>
            <span>
              <v-icon small left>mdi-clock-outline</v-icon>
              {{ article.readTime }} min lectura
            </span>
          </div>

          <!-- Imagen destacada -->
          <v-img
            :src="article.image"
            :alt="article.title"
            height="400"
            class="rounded-lg mb-6"
            cover
          ></v-img>

          <!-- Contenido -->
          <div class="article-content" v-html="article.content"></div>

          <!-- Etiquetas -->
          <div class="tags-section mt-8">
            <v-chip
              v-for="tag in article.tags"
              :key="tag"
              small
              class="mr-2 mb-2"
              outlined
            >
              #{{ tag }}
            </v-chip>
          </div>

          <!-- Autor -->
          <v-card class="author-card mt-8 pa-4" outlined>
            <v-row align="center">
              <v-col cols="auto">
                <v-avatar size="60">
                  <img :src="article.author.avatar" :alt="article.author.name" />
                </v-avatar>
              </v-col>
              <v-col>
                <h3 class="text-h6">{{ article.author.name }}</h3>
                <p class="text-caption text--secondary mb-2">{{ article.author.bio }}</p>
                <div class="d-flex">
                  <v-btn icon small class="mr-2">
                    <v-icon small>mdi-twitter</v-icon>
                  </v-btn>
                  <v-btn icon small class="mr-2">
                    <v-icon small>mdi-linkedin</v-icon>
                  </v-btn>
                  <v-btn icon small>
                    <v-icon small>mdi-web</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <div v-else class="text-center py-12">
          <v-icon size="64" color="grey lighten-2">mdi-file-document-outline</v-icon>
          <h2 class="headline mt-4">Artículo no encontrado</h2>
          <p class="subtitle-1 mt-2">
            El artículo que buscas no existe o ha sido eliminado.
          </p>
          <v-btn color="primary" class="mt-4" to="/blog"> Ver todos los artículos </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "BlogPost",
  data() {
    return {
      loading: true,
      article: null,
      articleId: null,
    };
  },
  mounted() {
    this.articleId = this.$route.params.id;
    this.loadArticle();
  },
  watch: {
    "$route.params.id"(newId) {
      this.articleId = newId;
      this.loadArticle();
    },
  },
  methods: {
    async loadArticle() {
      this.loading = true;
      try {
        // Aquí normalmente harías una llamada API
        // Por ahora usaremos datos de ejemplo
        const articles = {
          1: {
            id: 1,
            title: "Cómo Implementar Vue.js en Tu Proyecto",
            category: "Tecnología",
            date: "2024-01-15",
            readTime: 5,
            image: "https://picsum.photos/800/400?random=1",
            content: `
              <p>Vue.js es un framework progresivo para construir interfaces de usuario...</p>
              <h2>Ventajas de Vue.js</h2>
              <p>Una de las principales ventajas es su curva de aprendizaje suave...</p>
              <ul>
                <li>Fácil de aprender</li>
                <li>Documentación excelente</li>
                <li>Comunidad activa</li>
              </ul>
              <h2>Primeros pasos</h2>
              <p>Para empezar con Vue.js, simplemente agrega el script a tu HTML...</p>
            `,
            tags: ["Vue.js", "Frontend", "JavaScript"],
            author: {
              name: "Juan Pérez",
              avatar: "https://randomuser.me/api/portraits/men/32.jpg",
              bio: "Desarrollador frontend con 5 años de experiencia",
            },
          },
          2: {
            id: 2,
            title: "Mejores Prácticas para SEO en 2024",
            category: "Marketing",
            date: "2024-01-10",
            readTime: 8,
            image: "https://picsum.photos/800/400?random=2",
            content: "<p>El SEO sigue siendo fundamental para el éxito online...</p>",
            tags: ["SEO", "Marketing", "Digital"],
            author: {
              name: "María Gómez",
              avatar: "https://randomuser.me/api/portraits/women/44.jpg",
              bio: "Especialista en marketing digital",
            },
          },
          3: {
            id: 3,
            title: "Inteligencia Artificial en el Desarrollo Web",
            category: "IA",
            date: "2024-01-05",
            readTime: 10,
            image: "https://picsum.photos/800/400?random=3",
            content: `
              <h1>La IA está transformando el desarrollo web</h1>
              <p>Desde asistentes de código hasta testing automatizado...</p>
              <h2>Herramientas recomendadas</h2>
              <p>Aquí te presentamos las mejores herramientas de IA para desarrolladores...</p>
              <h2>Futuro del desarrollo web</h2>
              <p>La integración de IA cambiará radicalmente cómo construimos aplicaciones...</p>
            `,
            tags: ["IA", "Desarrollo", "Innovación"],
            author: {
              name: "Carlos Rodríguez",
              avatar: "https://randomuser.me/api/portraits/men/67.jpg",
              bio: "Investigador en inteligencia artificial",
            },
          },
        };

        // Buscar el artículo por ID
        this.article = articles[this.articleId];

        if (this.article) {
          // Cambiar título de la página
          document.title = `${this.article.title} | Tu Sitio`;
        }
      } catch (error) {
        console.error("Error cargando artículo:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString("es-ES", options);
    },
  },
};
</script>

<style scoped>
.blog-post-container {
  min-height: 70vh;
}

.article-meta {
  color: #666;
  font-size: 0.9rem;
}

.article-content {
  line-height: 1.8;
  font-size: 1.1rem;
}

.article-content >>> h1 {
  font-size: 2rem;
  margin: 1.5rem 0 1rem 0;
  color: #333;
}

.article-content >>> h2 {
  font-size: 1.5rem;
  margin: 1.2rem 0 0.8rem 0;
  color: #444;
}

.article-content >>> p {
  margin-bottom: 1rem;
}

.article-content >>> ul {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

.article-content >>> li {
  margin-bottom: 0.5rem;
}

.author-card {
  border-radius: 12px;
  background: #f9f9f9;
}
</style>
